import { AmountTier, PaidByEnum, PaymentTypeEnum, PricingQuoteIncludedProductDto, ProductTypeEnum, TieredFeeTypeEnum } from '@inosio/payments-sdk';
import { TableContainer, Table, TableHead, TableRow, TableCell, Typography, TableBody } from '@mui/material';
import NumberFormat from 'react-number-format';

export interface PricingQuoteConsumablesLineItem {
  label: string;
  amount: number | string;
  paidBy: PaidByEnum;
  hasAsterisk?: boolean;
}
export interface PricingQuoteConsumablesTableProps {
  /**
   * A list of products included in this package. Comes from a created pricing quote.
   */
  includedProducts: PricingQuoteIncludedProductDto[];
}

const PricingQuoteConsumablesTable = (props: PricingQuoteConsumablesTableProps) => {
  const buildDataForConsumablesTable = (allProducts: PricingQuoteIncludedProductDto[]) => {
    const consumableLineItems: PricingQuoteConsumablesLineItem[] = [];
    //get all products which have non zero FlatFee
    const allConsumableProducts = allProducts.filter((prod) => !!prod.ConsumableFee);
    const appProduct = allConsumableProducts.find((prod) => prod.ProductType === ProductTypeEnum.Applications);
    const screeningProduct = allConsumableProducts.find((prod) => prod.ProductType === ProductTypeEnum.Screening);

    //handle payments product differently
    const paymentsProduct = allProducts.find((prod) => prod.ProductType === ProductTypeEnum.Payments);
    const achConfig = paymentsProduct?.PaymentOptions?.find((config) => config.PaymentType === PaymentTypeEnum.Ach);
    const dbConfig = paymentsProduct?.PaymentOptions?.find((config) => config.PaymentType === PaymentTypeEnum.Db);
    const ccConfig = paymentsProduct?.PaymentOptions?.find((config) => config.PaymentType === PaymentTypeEnum.Cc);

    if (!!achConfig) {
      const lineItem: PricingQuoteConsumablesLineItem = {
        label: 'ACH Payment',
        amount: achConfig.Fee[0].Amount / 100, //take the amount from the first tier, theres only one for ACH
        paidBy: achConfig.PaidBy,
      };
      consumableLineItems.push(lineItem);
    }

    if (!!dbConfig) {
      const parsedTiers = parseDbTiers(dbConfig.Fee as AmountTier[]);
      const dbRange = getLowHighFeeString(parsedTiers);
      const lineItem: PricingQuoteConsumablesLineItem = {
        label: 'Debit Cards',
        amount: dbRange + ' †',
        paidBy: dbConfig.PaidBy,
      };
      consumableLineItems.push(lineItem);
    }

    if (!!ccConfig) {
      const lineItem: PricingQuoteConsumablesLineItem = {
        label: 'Credit Cards',
        amount: `${(ccConfig.Fee[0].Amount / 10).toFixed(1)}%` + (ccConfig.ConvenienceFee != null ? ` + $${((ccConfig.ConvenienceFee as number) / 100).toFixed(2)}` : ''),

        paidBy: ccConfig.PaidBy,
      };
      consumableLineItems.push(lineItem);
    }

    if (achConfig?.AdditionalFeatures && achConfig?.AdditionalFeatures?.length > 0) {
      achConfig.AdditionalFeatures.forEach((addFeat) => {
        const nsfLineItem: PricingQuoteConsumablesLineItem = {
          label: addFeat.Label,
          amount: addFeat.Amount / 100,
          paidBy: PaidByEnum.Payer,
        };
        consumableLineItems.push(nsfLineItem);
      });
    }

    if (!!appProduct) {
      const lineItem: PricingQuoteConsumablesLineItem = {
        label: 'Application (Per Adult)',
        amount: (appProduct.ConsumableFee as number) / 100,
        paidBy: PaidByEnum.Pm,
        hasAsterisk: true,
      };
      consumableLineItems.push(lineItem);
    }

    if (!!screeningProduct) {
      const lineItem: PricingQuoteConsumablesLineItem = {
        label: screeningProduct.ProductLabel ? `Screening Report: ${screeningProduct.ProductLabel}` : 'Screening',
        amount: (screeningProduct.ConsumableFee as number) / 100,
        paidBy: PaidByEnum.Pm,
      };
      consumableLineItems.push(lineItem);
    }

    return consumableLineItems;
  };

  const parseDbTiers = (tiers: AmountTier[]): { fee: number; feeType: TieredFeeTypeEnum; low: number; high?: number }[] => {
    return tiers?.map((tier, index) => {
      return { fee: tier.Amount, low: tier.Floor === 0 ? 0 : tier.Floor + 1, high: tiers?.[index + 1]?.Floor, feeType: tier.FeeType };
    });
  };

  /**
   * Given fee tiers this gives the lowest and highest fee as a range
   * @param tiers
   * @returns
   */
  const getLowHighFeeString = (tiers: { fee: number; low: number; high?: number; feeType: string }[]): string => {
    if (!tiers || tiers.length === 0) {
      return '$0.00-$0.00'; // Handle empty case
    }

    // Exclude tiers with FeeType 'P' (Percentage)
    const filteredTiers = tiers.filter((tier) => tier.feeType !== 'P');

    if (filteredTiers.length === 0) {
      return '$0.00-$0.00'; // If all tiers are excluded, return default
    }

    // Find the tier with the lowest `low` and the tier with the highest `high`
    const lowestTier = filteredTiers.reduce((acc, tier) => (tier.low < acc.low ? tier : acc), filteredTiers[0]);
    const highestTier = filteredTiers.reduce((acc, tier) => {
      const accHigh = acc.high !== undefined ? acc.high : acc.low;
      const tierHigh = tier.high !== undefined ? tier.high : tier.low;
      return tierHigh > accHigh ? tier : acc;
    }, filteredTiers[0]);

    // Extract and format the fees
    const lowFee = (lowestTier.fee / 100).toFixed(2);
    const highFee = (highestTier.fee / 100).toFixed(2);

    return `$${lowFee} - $${highFee}`;
  };

  return (
    <TableContainer>
      <Table
        sx={{
          borderCollapse: 'collapse', // Ensures borders collapse
          '& th, & td': {
            border: '1px solid #E0E0E0', // Vertical and horizontal borders
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold' }}>Item</Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold' }}>Price</Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold' }}>Paid By</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {buildDataForConsumablesTable(props.includedProducts).map((consumable, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography sx={{ fontFamily: 'Work Sans !important' }}>{consumable.label}</Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: 'Work Sans !important' }}>
                  {typeof consumable.amount === 'number' ? (
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={consumable.amount}
                      prefix="$"
                      decimalSeparator="."
                      suffix={consumable.hasAsterisk ? '‡' : ''}
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  ) : (
                    consumable.amount
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: 'Work Sans !important' }}>{consumable.paidBy}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PricingQuoteConsumablesTable;
